import { clone } from "./projects";
import {
  pins,
  isSequential,
  uid,
  pinLabel,
  nodeSize,
} from "../engine/registry";
export function normalizeClocks(project) {
  const p = clone(project);
  for (const e of p.connections) {
    const clock = p.components.find(
      (c) => c.id === e.from.component && c.type === "CLOCK",
    );
    const c = p.components.find((c) => c.id === e.to.component);
    if (
      !clock?.config.activation ||
      !c ||
      !isSequential(c) ||
      !["CLK", "EN"].includes(e.to.pin)
    )
      continue;
    const level = clock.config.activation.endsWith("LEVEL");
    if (level && !c.type.startsWith("SR"))
      throw new Error(
        "Level clocks drive SR latches. Connect an edge clock to a D, T or JK flip-flop.",
      );
    if (c.type.startsWith("SR")) {
      c.type = level ? "SR_LATCH" : "SR_FF";
      e.to.pin = level ? "EN" : "CLK";
    }
    c.config.trigger = clock.config.activation.startsWith("NEG")
      ? "NEG"
      : "POS";
  }
  return p;
}
export function moveComponents(project, updates) {
  const p = clone(project),
    byId = new Map(updates.map((u) => [u.id, u]));
  p.connections.forEach((e) => {
    const dest = byId.get(e.to.component),
      source = byId.get(e.from.component);
    const old = p.components.find((c) => c.id === e.to.component);
    if (dest && old) {
      const dx = dest.x - old.x,
        dy = dest.y - old.y;
      if (e.anchors && (source || e.from.input || "constant" in e.from))
        e.anchors = {
          start: { x: e.anchors.start.x + dx, y: e.anchors.start.y + dy },
          end: { x: e.anchors.end.x + dx, y: e.anchors.end.y + dy },
        };
      if (e.from.input || "constant" in e.from) {
        if (e.terminal) {
          e.terminal.x += dx;
          e.terminal.y += dy;
        }
        if (e.waypoints)
          e.waypoints = e.waypoints.map((v) => ({ x: v.x + dx, y: v.y + dy }));
      } else if (source && e.waypoints)
        e.waypoints = e.waypoints.map((v) => ({ x: v.x + dx, y: v.y + dy }));
    }
  });
  p.components = p.components.map((c) => ({ ...c, ...byId.get(c.id) }));
  return p;
}
export function renamePin(project, componentId, pin, label) {
  const p = clone(project),
    c = p.components.find((c) => c.id === componentId);
  c.config.pinLabels = { ...c.config.pinLabels, [pin]: label };
  // A directly connected primary terminal has one name throughout its fan-out.
  const wire = p.connections.find(
    (e) => e.to.component === componentId && e.to.pin === pin,
  );
  if (wire?.from.input) {
    const input = p.inputs.find((i) => i.id === wire.from.input);
    if (input) input.name = label;
  }
  return p;
}
export function attachMissingTerminals(project) {
  const p = clone(project),
    taken = new Set([
      ...p.inputs.map((i) => i.name),
      ...p.components.map((c) => c.name),
    ]);
  for (const c of p.components)
    for (const pin of pins(c).inputs) {
      if (
        pin.control ||
        p.connections.some(
          (e) => e.to.component === c.id && e.to.pin === pin.id,
        )
      )
        continue;
      let name = isSequential(c)
          ? pinLabel(c, pin.id)
          : `P${p.inputs.length + 1}`,
        i = 2,
        base = name;
      while (taken.has(name)) name = `${base}_${i++}`;
      taken.add(name);
      const input = { id: uid("input"), name, values: [0] };
      p.inputs.push(input);
      p.connections.push({
        id: uid("wire"),
        from: { input: input.id },
        to: { component: c.id, pin: pin.id },
        width: 1,
      });
      if (isSequential(c))
        c.config.pinLabels = { ...c.config.pinLabels, [pin.id]: name };
    }
  return p;
}
export function resizeComponent(project, id, w, h) {
  const p = clone(project),
    c = p.components.find((c) => c.id === id);
  if (c)
    c.size = {
      w: Math.max(24, Math.min(800, w)),
      h: Math.max(24, Math.min(1200, h)),
    };
  return p;
}
export const selectionBounds = (project, ids) => {
  const cs = project.components.filter((c) => ids.includes(c.id));
  if (!cs.length) return null;
  const x = Math.min(...cs.map((c) => c.x)) - 16,
    y = Math.min(...cs.map((c) => c.y)) - 16;
  return {
    x,
    y,
    w: Math.max(...cs.map((c) => c.x + nodeSize(c).w)) + 16 - x,
    h: Math.max(...cs.map((c) => c.y + nodeSize(c).h)) + 16 - y,
  };
};
